import { motion } from "framer-motion";
import { useTheme } from "@/contexts/ThemeContext";
import { Card, CardContent } from "./ui/card";
import { Target, Heart, Zap, Globe } from "lucide-react";

export const OurMission = () => {
  const { colors, theme } = useTheme();

  const missionPoints = [
    {
      icon: Target,
      title: "Strategic Excellence",
      description: "Delivering data-driven strategies that transform brand narratives into viral success stories."
    },
    {
      icon: Heart,
      title: "Authentic Connections",
      description: "Building genuine relationships between brands and audiences through meaningful content."
    },
    {
      icon: Zap,
      title: "Innovation First",
      description: "Pioneering cutting-edge approaches to influencer marketing and digital storytelling."
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Creating campaigns that resonate across cultures and drive worldwide engagement."
    }
  ];

  return (
    <section 
      id="our-mission" 
      className="py-16 md:py-24"
      style={{ backgroundColor: colors.background }}
    >
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }} // Much faster initial animation
          className="text-center mb-16"
        >
          <h2 
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{
              background: colors.gradient,
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Mission
          </h2>
          <motion.div
            className="w-24 h-1 mx-auto mb-6"
            style={{ backgroundColor: colors.primary }}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15, ease: "easeOut" }} // Faster with smooth easing
          />
          <p 
            className="text-lg md:text-xl max-w-4xl mx-auto leading-relaxed"
            style={{ color: colors.secondary }}
          >
            To empower filmmakers, creators, and media brands with intelligent strategies in production, marketing, and talent management. We combine data, creativity, and technology to build sustainable success in the dynamic world of entertainment.
            <br />
            <br />
            At Agape, we strive to amplify voices, nurture stories, and champion cinematic excellence-one frame at a time.
          </p>
        </motion.div>

        {/* Optimized "Beyond Marketing" Box with Smooth Animations */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }} // Faster with smooth easing
          className="mt-16 relative"
          style={{ padding: '20px 0' }}
        >
          {/* Responsive glow effects */}
          <motion.div 
            className="absolute inset-0 rounded-2xl opacity-12 group-hover:opacity-20 transition-all duration-200 ease-out" // Much faster response
            style={{
              background: `radial-gradient(circle at center, ${colors.primary}05, ${colors.secondary}03, transparent 70%)`,
              filter: 'blur(35px)',
              transform: 'scale(1.2)',
              boxShadow: `0 0 30px ${colors.primary}05, 0 0 50px ${colors.primary}02`
            }}
          />
          
          <motion.div 
            className="absolute inset-0 rounded-2xl opacity-15 group-hover:opacity-25 transition-all duration-150 ease-out" // Very fast response
            style={{
              background: `linear-gradient(135deg, ${colors.primary}04, ${colors.secondary}03)`,
              filter: 'blur(20px)',
              transform: 'scale(1.08)'
            }}
          />
          
          <motion.div
            whileHover={{ 
              scale: 1.01,
              y: -2,
              transition: { duration: 0.15, type: "spring", stiffness: 400, damping: 25 } // Much faster spring
            }}
            className="relative backdrop-blur-xl border-2 rounded-2xl shadow-2xl group"
            style={{ 
              backgroundColor: `${colors.card}98`,
              borderColor: `${colors.primary}65`,
              boxShadow: `
                0 15px 30px ${colors.primary}08, 
                0 8px 20px ${colors.primary}05, 
                0 4px 12px ${colors.primary}03,
                inset 0 1px 2px ${colors.primary}12,
                inset 0 -1px 2px ${colors.secondary}06
              `,
              backdropFilter: 'blur(15px) saturate(1.5)',
            }}
          >
            <div className="p-10 md:p-14">
              {/* Fast-responding inner effects */}
              <div 
                className="absolute inset-0 rounded-2xl opacity-8 group-hover:opacity-15 transition-opacity duration-150 ease-out" // Very fast
                style={{
                  background: `radial-gradient(circle at 30% 30%, ${colors.primary}08 0%, ${colors.secondary}04 30%, transparent 70%)`
                }}
              />
              
              <div 
                className="absolute inset-2 rounded-xl opacity-12 group-hover:opacity-20 transition-opacity duration-100 ease-out" // Ultra fast
                style={{
                  background: `linear-gradient(135deg, ${colors.background}08, ${colors.card}04, ${colors.primary}02)`,
                  backdropFilter: 'blur(8px)',
                }}
              />
              
              <motion.div
                className="relative z-10 max-w-4xl mx-auto text-center"
                animate={{
                  backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"]
                }}
                transition={{
                  duration: 8, // Faster animation cycle
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <h3 
                  className="text-2xl md:text-3xl font-bold mb-6"
                  style={{ 
                    color: colors.primary,
                    textShadow: `0 2px 4px ${colors.primary}15`
                  }}
                >
                  Beyond Marketing - We Create Movements
                </h3>
                <p 
                  className="text-lg md:text-xl max-w-4xl leading-relaxed"
                  style={{ 
                    color: colors.secondary,
                    textShadow: `0 1px 2px ${colors.foreground}05`
                  }}
                >
                  At Agape Entertainment, we don't just create campaigns - we craft experiences that 
                  inspire action, drive engagement, and build communities. Our mission extends beyond 
                  traditional marketing to create authentic movements that resonate with audiences 
                  and deliver measurable results for our partners.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};